import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { 
  ArrowRight, 
  CheckCircle, 
  Car,
  Footprints,
  Home,
  Phone,
  MessageCircle,
  ChevronDown,
  Award,
  Clock,
  Star,
  Shield,
  Zap,
  Heart,
  Quote
} from 'lucide-react';
import { brandTheme } from '../styles/BrandTheme';
import HeroSectionComponent from '../components/HeroSection';

const HeroSection = styled.section`
  height: 100vh;
  background: 
    linear-gradient(135deg, rgba(0, 0, 0, 0.4) 0%, rgba(0, 0, 0, 0.6) 100%),
    url('/images/resin-surfaces-bound-work1.jpeg') center/cover;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  text-align: center;
  position: relative;
  overflow: hidden;
  padding: 0 1rem;

  /* iPad Pro and Large Tablets */
  @media (max-width: 1366px) and (min-width: 1025px) {
    height: 100vh;
    padding: 0 2rem;
  }

  /* iPad Air and Medium Tablets */
  @media (max-width: 1024px) and (min-width: 769px) {
    height: 100vh;
    padding: 0 1.5rem;
  }

  /* Landscape Mobile and Small Tablets */
  @media (max-width: 768px) and (min-width: 569px) and (orientation: landscape) {
    height: 100vh;
    padding: 0 1.25rem;
  }

  /* Portrait Mobile - iPhone 12 Pro Max and Large Mobile */
  @media (max-width: 768px) and (min-width: 428px) and (orientation: portrait) {
    height: 100vh;
    padding: 0 1.25rem;
  }

  /* iPhone 12 and Standard Mobile */
  @media (max-width: 427px) and (min-width: 390px) {
    height: 100vh;
    padding: 0 1rem;
  }

  /* iPhone SE and Small Mobile */
  @media (max-width: 389px) and (min-width: 320px) {
    height: 100vh;
    padding: 0 0.75rem;
  }

  /* Landscape orientation adjustments */
  @media (orientation: landscape) and (max-height: 500px) {
    height: 100vh;
    padding: 0 1rem;
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(45deg, transparent 30%, rgba(254, 206, 27, 0.1) 50%, transparent 70%);
    animation: shimmer 3s ease-in-out infinite;
  }

  @keyframes shimmer {
    0%, 100% { transform: translateX(-100%); }
    50% { transform: translateX(100%); }
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="grain" width="100" height="100" patternUnits="userSpaceOnUse"><circle cx="50" cy="50" r="0.5" fill="rgba(255,255,255,0.1)"/></pattern></defs><rect width="100" height="100" fill="url(%23grain)"/></svg>'),
      url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 200"><defs><pattern id="dots" width="20" height="20" patternUnits="userSpaceOnUse"><circle cx="10" cy="10" r="1" fill="rgba(254,206,27,0.1)"/></pattern></defs><rect width="200" height="200" fill="url(%23dots)"/></svg>');
    opacity: 0.4;
    pointer-events: none;
    animation: float 20s ease-in-out infinite;
  }

  @keyframes float {
    0%, 100% { transform: translateY(0px) rotate(0deg); }
    50% { transform: translateY(-10px) rotate(1deg); }
  }
`;

const HeroContent = styled.div`
  max-width: 1000px;
  width: 100%;
  padding: 2rem 1rem;
  position: relative;
  z-index: 2;
  background: rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.3);
  margin-top: 80px; /* Account for fixed header */
  margin-bottom: 2rem;

  @media (max-width: 1024px) {
    padding: 1.5rem;
    margin-top: 70px;
    margin-bottom: 1.5rem;
  }

  @media (max-width: 768px) {
    padding: 1.25rem;
    margin-top: 60px;
    margin-bottom: 1rem;
    border-radius: 15px;
  }

  @media (max-width: 480px) {
    padding: 1rem;
    margin-top: 50px;
    margin-bottom: 1rem;
    border-radius: 12px;
  }

  @media (max-width: 375px) {
    padding: 0.75rem;
    margin-top: 40px;
    margin-bottom: 0.75rem;
    border-radius: 10px;
  }
`;

const HeroBadge = styled(motion.div)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(254, 206, 27, 0.2);
  backdrop-filter: blur(15px);
  border: 1px solid rgba(254, 206, 27, 0.4);
  padding: 0.75rem 1.5rem;
  border-radius: 50px;
  margin-bottom: 2rem;
  font-size: 0.9rem;
  font-weight: 600;
  color: #FECE1B;
  box-shadow: 0 10px 30px rgba(254, 206, 27, 0.2);

  svg {
    color: #FECE1B;
    filter: drop-shadow(0 0 8px rgba(254, 206, 27, 0.5));
  }

  @media (max-width: 768px) {
    padding: 0.625rem 1.25rem;
    font-size: 0.85rem;
  }

  @media (max-width: 375px) {
    padding: 0.5rem 1rem;
    font-size: 0.8rem;
    margin-bottom: 1.5rem;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;
  width: 100%;
  overflow-x: hidden;

  @media (max-width: 1024px) {
    padding: 0 1.5rem;
  }

  @media (max-width: 768px) {
    padding: 0 1rem;
  }

  @media (max-width: 480px) {
    padding: 0 0.75rem;
  }

  @media (max-width: 375px) {
    padding: 0 0.5rem;
  }
`;

const HeroTitle = styled(motion.h1)`
  font-size: clamp(2.5rem, 6vw, 5rem);
  margin-bottom: 1.5rem;
  font-weight: ${brandTheme.typography.fontWeight.extrabold};
  line-height: ${brandTheme.typography.lineHeight.tight};
  background: linear-gradient(135deg, #FECE1B 0%, #ffffff 50%, #FECE1B 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  text-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);
  font-family: ${brandTheme.typography.fontFamily.heading};
  filter: drop-shadow(0 0 20px rgba(254, 206, 27, 0.3));
  letter-spacing: -0.02em;

  @media (max-width: 768px) {
    margin-bottom: 1rem;
    letter-spacing: -0.01em;
  }

  @media (max-width: 375px) {
    font-size: clamp(1.5rem, 5vw, 2rem);
    margin-bottom: 0.75rem;
    line-height: 1.1;
  }
`;

const HeroSubtitle = styled(motion.p)`
  font-size: clamp(1.2rem, 3vw, 1.6rem);
  margin-bottom: 3rem;
  opacity: 0.95;
  font-weight: 400;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.6;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
  color: rgba(255, 255, 255, 0.95);

  @media (max-width: 768px) {
    margin-bottom: 2rem;
    font-size: clamp(1.1rem, 4vw, 1.4rem);
    line-height: 1.5;
  }

  @media (max-width: 375px) {
    margin-bottom: 1.5rem;
    font-size: clamp(1rem, 3.5vw, 1.2rem);
    line-height: 1.4;
  }
`;

const CTAButtons = styled(motion.div)`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;
  justify-content: center;
  margin-bottom: 4rem;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 1.25rem;
    margin-bottom: 3rem;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1rem;
    margin-bottom: 2.5rem;
  }

  @media (max-width: 480px) {
    gap: 0.875rem;
    margin-bottom: 2rem;
  }

  @media (max-width: 375px) {
    gap: 0.75rem;
    margin-bottom: 1.5rem;
  }
`;

const StyledButton = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding: 1.25rem 2rem;
  border-radius: 50px;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  font-size: 1rem;
  position: relative;
  overflow: hidden;
  text-align: center;
  min-height: 60px;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    transition: left 0.5s;
  }

  &:hover::before {
    left: 100%;
  }

  &.primary {
    background: linear-gradient(135deg, #FECE1B 0%, #d97706 100%);
    color: #000000;
    box-shadow: 0 15px 30px rgba(254, 206, 27, 0.3), 0 0 0 1px rgba(254, 206, 27, 0.2);

    &:hover {
      transform: translateY(-3px) scale(1.02);
      box-shadow: 0 20px 40px rgba(254, 206, 27, 0.4), 0 0 0 1px rgba(254, 206, 27, 0.3);
      color: #000000;
    }
  }

  &.secondary {
    background: rgba(0, 0, 0, 0.1);
    color: white;
    border: 2px solid rgba(254, 206, 27, 0.5);
    backdrop-filter: blur(20px);

    &:hover {
      background: rgba(254, 206, 27, 0.2);
      border-color: rgba(254, 206, 27, 0.8);
      transform: translateY(-2px);
    }
  }

  &.whatsapp {
    background: linear-gradient(135deg, #25d366 0%, #20c55a 100%);
    color: white;
    box-shadow: 0 15px 30px rgba(37, 211, 102, 0.3);

    &:hover {
      background: linear-gradient(135deg, #20c55a 0%, #16a34a 100%);
      transform: translateY(-3px) scale(1.02);
      box-shadow: 0 20px 40px rgba(37, 211, 102, 0.4);
    }
  }

  @media (max-width: 1024px) {
    padding: 1rem 1.5rem;
    font-size: 0.95rem;
    min-height: 55px;
  }

  @media (max-width: 768px) {
    padding: 1rem 2rem;
    font-size: 1rem;
    min-height: 50px;
  }

  @media (max-width: 480px) {
    padding: 0.875rem 1.5rem;
    font-size: 0.9rem;
    min-height: 48px;
  }

  @media (max-width: 375px) {
    padding: 0.75rem 1.25rem;
    font-size: 0.85rem;
    min-height: 44px;
  }
`;

const ScrollIndicator = styled(motion.div)`
  position: absolute;
  bottom: 2rem;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  color: #FECE1B;
  cursor: pointer;
  transition: all 0.3s ease;
  background: rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(10px);
  padding: 0.75rem 1rem;
  border-radius: 25px;
  border: 1px solid rgba(254, 206, 27, 0.3);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.2);

  &:hover {
    color: #FECE1B;
    transform: translateX(-50%) translateY(-5px);
    background: rgba(254, 206, 27, 0.1);
    box-shadow: 0 12px 35px rgba(254, 206, 27, 0.2);
  }

  span {
    font-size: 0.85rem;
    font-weight: 600;
    text-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
  }

  svg {
    filter: drop-shadow(0 0 8px rgba(254, 206, 27, 0.5));
  }

  @media (max-width: 768px) {
    bottom: 1.5rem;
    padding: 0.5rem 0.75rem;
  }

  @media (max-width: 375px) {
    bottom: 1rem;
    padding: 0.4rem 0.6rem;
    min-height: 44px;
    min-width: 44px;
  }
`;

const Section = styled.section`
  padding: ${brandTheme.spacing[20]} 0;

  &:nth-child(even) {
    background: ${brandTheme.colors.neutral.gray[50]};
  }
`;

const SectionTitle = styled.h2`
  text-align: center;
  margin-bottom: ${brandTheme.spacing[16]};
  font-size: clamp(2rem, 4vw, 3rem);
  font-weight: ${brandTheme.typography.fontWeight.bold};
  color: ${brandTheme.colors.secondary[900]};
  font-family: ${brandTheme.typography.fontFamily.heading};
`;

const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: ${brandTheme.spacing[8]};
  margin-bottom: ${brandTheme.spacing[16]};
`;

const ServiceCard = styled(motion.div)`
  background: ${brandTheme.colors.neutral.white};
  border-radius: ${brandTheme.borderRadius['2xl']};
  padding: ${brandTheme.spacing[8]};
  box-shadow: ${brandTheme.boxShadow.lg};
  border: 1px solid ${brandTheme.colors.neutral.gray[200]};
  transition: all ${brandTheme.animation.duration.normal} ${brandTheme.animation.easing.smooth};
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #FECE1B, #d97706);
  }

  &:hover {
    transform: translateY(-8px);
    box-shadow: ${brandTheme.boxShadow['2xl']};
  }
`;

const ServiceIcon = styled.div`
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #FECE1B 0%, #d97706 100%);
  border-radius: ${brandTheme.borderRadius.xl};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: ${brandTheme.spacing[6]};
  color: #000000;
  box-shadow: 0 8px 25px rgba(254, 206, 27, 0.3);
`;

const ServiceTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: ${brandTheme.typography.fontWeight.bold};
  color: ${brandTheme.colors.secondary[900]};
  margin-bottom: ${brandTheme.spacing[4]};
  font-family: ${brandTheme.typography.fontFamily.heading};
`;

const ServiceDescription = styled.p`
  color: ${brandTheme.colors.secondary[600]};
  line-height: ${brandTheme.typography.lineHeight.relaxed};
  margin-bottom: ${brandTheme.spacing[6]};
`;

const ServiceFeatures = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 ${brandTheme.spacing[6]} 0;

  li {
    display: flex;
    align-items: center;
    gap: ${brandTheme.spacing[2]};
    margin-bottom: ${brandTheme.spacing[2]};
    color: ${brandTheme.colors.secondary[600]};
    font-size: 0.95rem;

    svg {
      color: #FECE1B;
      flex-shrink: 0;
    }
  }
`;

const ServiceButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: ${brandTheme.spacing[2]};
  padding: ${brandTheme.spacing[3]} ${brandTheme.spacing[6]};
  background: linear-gradient(135deg, #FECE1B 0%, #d97706 100%);
  color: #000000;
  text-decoration: none;
  border-radius: ${brandTheme.borderRadius.full};
  font-weight: ${brandTheme.typography.fontWeight.semibold};
  transition: all ${brandTheme.animation.duration.normal} ${brandTheme.animation.easing.smooth};
  box-shadow: 0 4px 15px rgba(254, 206, 27, 0.3);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(254, 206, 27, 0.4);
  }
`;

const ProcessSection = styled.div`
  background: ${brandTheme.colors.neutral.white};
  border-radius: ${brandTheme.borderRadius['2xl']};
  padding: ${brandTheme.spacing[12]};
  box-shadow: ${brandTheme.boxShadow.xl};
  margin-top: ${brandTheme.spacing[12]};
`;

const ProcessGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: ${brandTheme.spacing[8]};
`;

const ProcessStep = styled(motion.div)`
  text-align: center;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    top: 40px;
    right: -${brandTheme.spacing[4]};
    width: 100%;
    height: 2px;
    background: linear-gradient(90deg, #FECE1B, transparent);
    z-index: 1;

    @media (max-width: 768px) {
      display: none;
    }
  }

  &:last-child::after {
    display: none;
  }
`;

const StepNumber = styled.div`
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #FECE1B 0%, #d97706 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto ${brandTheme.spacing[4]} auto;
  color: #000000;
  font-size: 1.5rem;
  font-weight: ${brandTheme.typography.fontWeight.bold};
  box-shadow: 0 8px 25px rgba(254, 206, 27, 0.3);
  position: relative;
  z-index: 2;
`;

const StepTitle = styled.h4`
  font-size: 1.25rem;
  font-weight: ${brandTheme.typography.fontWeight.semibold};
  color: ${brandTheme.colors.secondary[900]};
  margin-bottom: ${brandTheme.spacing[2]};
  font-family: ${brandTheme.typography.fontFamily.heading};
`;

const StepDescription = styled.p`
  color: ${brandTheme.colors.secondary[600]};
  line-height: ${brandTheme.typography.lineHeight.relaxed};
`;

const PartnershipSection = styled.section`
  background: linear-gradient(135deg, ${brandTheme.colors.neutral.gray[50]} 0%, ${brandTheme.colors.neutral.gray[100]} 100%);
  padding: ${brandTheme.spacing[20]} 0;
`;

const PartnershipsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(450px, 1fr));
  gap: ${brandTheme.spacing[8]};
  margin-top: ${brandTheme.spacing[8]};

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: ${brandTheme.spacing[6]};
  }
`;

const PartnershipCard = styled.div`
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: ${brandTheme.spacing[6]};
  align-items: center;
  background: ${brandTheme.colors.neutral.white};
  padding: ${brandTheme.spacing[8]};
  border-radius: ${brandTheme.borderRadius['2xl']};
  box-shadow: ${brandTheme.boxShadow.xl};
  border: 1px solid ${brandTheme.colors.neutral.gray[200]};
  transition: all ${brandTheme.animation.duration.normal} ${brandTheme.animation.easing.smooth};

  &:hover {
    transform: translateY(-5px);
    box-shadow: ${brandTheme.boxShadow['2xl']};
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: ${brandTheme.spacing[4]};
    padding: ${brandTheme.spacing[6]};
  }
`;

const PartnershipLogo = styled.div`
  text-align: center;
  
  img {
    max-width: 150px;
    height: auto;
    filter: drop-shadow(0 10px 20px rgba(0, 0, 0, 0.1));
    
    @media (max-width: 768px) {
      max-width: 120px;
    }
  }
`;

const PartnershipDetails = styled.div`
  h3 {
    color: ${brandTheme.colors.secondary[900]};
    margin-bottom: 0.5rem;
    font-size: 1.375rem;
    font-weight: ${brandTheme.typography.fontWeight.bold};
    font-family: ${brandTheme.typography.fontFamily.heading};
  }

  .partner-tagline {
    color: ${brandTheme.colors.secondary[600]};
    font-size: 0.9rem;
    font-weight: ${brandTheme.typography.fontWeight.medium};
    margin-bottom: ${brandTheme.spacing[4]};
    font-style: italic;
  }

  p {
    color: ${brandTheme.colors.secondary[600]};
    line-height: ${brandTheme.typography.lineHeight.relaxed};
    margin-bottom: ${brandTheme.spacing[4]};
    font-size: 1rem;

    a {
      color: #FECE1B;
      text-decoration: none;
      font-weight: ${brandTheme.typography.fontWeight.semibold};
      
      &:hover {
        text-decoration: underline;
      }
    }
  }
`;

const PartnershipBenefits = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${brandTheme.spacing[2]};
  margin-bottom: ${brandTheme.spacing[4]};
`;

const PartnershipBenefit = styled.div`
  display: flex;
  align-items: center;
  gap: ${brandTheme.spacing[2]};
  color: ${brandTheme.colors.secondary[600]};
  font-weight: ${brandTheme.typography.fontWeight.medium};
  font-size: 0.9rem;

  svg {
    color: #FECE1B;
    flex-shrink: 0;
  }
`;

const PartnershipCTA = styled.div`
  margin-top: ${brandTheme.spacing[4]};
`;

const PartnerLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: ${brandTheme.spacing[2]};
  padding: ${brandTheme.spacing[3]} ${brandTheme.spacing[6]};
  background: linear-gradient(135deg, #FECE1B 0%, #d97706 100%);
  color: #000000;
  text-decoration: none;
  border-radius: ${brandTheme.borderRadius.full};
  font-weight: ${brandTheme.typography.fontWeight.semibold};
  font-size: 0.9rem;
  transition: all ${brandTheme.animation.duration.normal} ${brandTheme.animation.easing.smooth};
  box-shadow: 0 4px 15px rgba(254, 206, 27, 0.3);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(254, 206, 27, 0.4);
  }
`;

const CTAButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: ${brandTheme.spacing[3]};
  padding: ${brandTheme.spacing[4]} ${brandTheme.spacing[8]};
  background: linear-gradient(135deg, #FECE1B 0%, #d97706 100%);
  color: #000000;
  text-decoration: none;
  border-radius: ${brandTheme.borderRadius.full};
  font-weight: ${brandTheme.typography.fontWeight.semibold};
  font-size: 1.125rem;
  transition: all ${brandTheme.animation.duration.normal} ${brandTheme.animation.easing.smooth};
  box-shadow: 0 8px 30px rgba(254, 206, 27, 0.3);
  margin: 0 auto;
  text-align: center;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 15px 40px rgba(254, 206, 27, 0.4);
  }
`;

const SectionSubtitle = styled.p`
  text-align: center;
  font-size: clamp(1rem, 2.5vw, 1.25rem);
  color: ${brandTheme.colors.neutral.gray[600]};
  max-width: 600px;
  margin: 0 auto 3rem auto;
  line-height: 1.6;
`;

const FAQSection = styled.section`
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  padding: 6rem 0;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23e2e8f0' fill-opacity='0.4'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E") repeat;
    opacity: 0.3;
  }

  @media (max-width: 768px) {
    padding: 4rem 0;
  }
`;

const FAQGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
  max-width: 800px;
  margin: 0 auto;
`;

const FAQItem = styled(motion.div)`
  background: white;
  border-radius: 15px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid #e2e8f0;
  overflow: hidden;
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
    transform: translateY(-2px);
  }

  .question {
    padding: 1.5rem 2rem;
    cursor: pointer;
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-weight: 600;
    font-size: 1.1rem;
    color: #FECE1B;
    transition: all 0.3s ease;
    background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);

    &:hover {
      background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
    }

    svg {
      transition: transform 0.3s ease;
      color: #FECE1B;
    }

    &.active svg {
      transform: rotate(180deg);
    }
  }

  .answer {
    padding: 0 2rem;
    max-height: 0;
    overflow: hidden;
    transition: all 0.3s ease;
    background: white;
    color: ${brandTheme.colors.neutral.gray[700]};
    line-height: 1.6;

    &.active {
      padding: 0 2rem 1.5rem 2rem;
      max-height: 200px;
    }
  }

  @media (max-width: 768px) {
    .question {
      padding: 1.25rem 1.5rem;
      font-size: 1rem;
    }

    .answer {
      &.active {
        padding: 0 1.5rem 1.25rem 1.5rem;
      }
    }
  }
`;

const ServicesPage: React.FC = () => {
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);

  const services = [
    {
      icon: Car,
      title: 'Resin Driveways',
      description: 'Heavy-duty resin bound driveways that stay puddle-free, boost kerb appeal and handle daily traffic with ease.',
      features: [
        'Permeable & SUDS compliant',
        'Fast curing & vehicle ready',
        'Bespoke borders & patterns',
        '10+ year workmanship warranty',
        'Anti-slip finish'
      ],
      link: '/resin-driveways-london/'
    },
    {
      icon: Home,
      title: 'Resin Patios & Courtyards',
      description: 'Seamless, barefoot-friendly patios and courtyards that connect garden rooms, pergolas and seating areas.',
      features: [
        'UV-stable resin blends',
        'Cool underfoot in summer',
        'Matches steps & planters',
        'Low-maintenance finish',
        'Wide colour palette'
      ],
      link: '/resin-patios-london/'
    },
    {
      icon: Footprints,
      title: 'Resin Paving & Pathways',
      description: 'Elegant, slip-resistant paving for entrances, walkways, ramps and garden paths that stays clean year-round.',
      features: [
        'SUDS compliant paving',
        'Slip-resistant texture',
        'Works over existing bases',
        'Quick & tidy installation',
        'Custom edging options'
      ],
      link: '/resin-paving-london/'
    }
  ];

  const processSteps = [
    {
      number: '01',
      title: 'Free Consultation',
      description: 'We visit your property to assess the area and discuss your requirements and preferences.'
    },
    {
      number: '02',
      title: 'Design & Quote',
      description: 'We provide a detailed design proposal and transparent pricing with no hidden costs.'
    },
    {
      number: '03',
      title: 'Professional Installation',
      description: 'Our experienced team installs your resin surface with precision and attention to detail.'
    },
    {
      number: '04',
      title: 'Quality Assurance',
      description: 'We conduct thorough quality checks and provide maintenance guidance for long-lasting results.'
    }
  ];

  const faqs = [
    {
      question: 'What resin surfacing services do you offer?',
      answer: 'We specialise in resin bound driveways, patios, courtyards, paving, pool decks and commercial installations. Our team delivers residential and commercial projects across London, Surrey, Hertfordshire, Kent, Essex, Buckinghamshire, and Berkshire.'
    },
    {
      question: 'How long does resin surfacing installation take?',
      answer: 'Most residential projects take 1-3 days to complete, depending on size and complexity. Commercial projects may take longer. We provide detailed timelines during our free consultation and keep you updated throughout the process.'
    },
    {
      question: 'What makes your resin surfacing different?',
      answer: 'We use only premium materials from Vuba Surfaces (UK\'s #1 manufacturer) and GCL Products. Our team has 10+ years of experience, we provide comprehensive warranties, and we\'re committed to customer satisfaction with a 5-star average rating.'
    },
    {
      question: 'Do you provide free quotes and surveys?',
      answer: 'Yes! We provide completely free, no-obligation quotes and site surveys for all resin surfacing projects. Our expert team will visit your property, assess the site conditions, and provide detailed pricing with no hidden costs.'
    },
    {
      question: 'What areas do you cover for resin surfacing?',
      answer: 'We serve all London boroughs, Surrey, Hertfordshire, Buckinghamshire, Kent, Essex, Berkshire, and Oxfordshire. Our service area includes Richmond, Kingston, Croydon, St Albans, Watford, Hemel Hempstead, Canterbury, Maidstone, Chelmsford, Colchester, Aylesbury, High Wycombe, Reading, Windsor, and many more locations.'
    },
    {
      question: 'What maintenance does resin surfacing require?',
      answer: 'Resin surfacing requires minimal maintenance. Regular sweeping and occasional pressure washing is usually sufficient. We provide a comprehensive maintenance guide with your installation and offer ongoing support to ensure your surface looks great for years to come.'
    },
    {
      question: 'Are you fully insured and qualified?',
      answer: 'Yes, we are fully insured and qualified resin surfacing specialists with over 10 years of experience. We have comprehensive public liability insurance, professional indemnity insurance, and all necessary qualifications.'
    },
    {
      question: 'What is the difference between resin bound and resin bonded?',
      answer: 'Resin bound surfacing has aggregates mixed throughout the resin, creating a smooth, permeable surface. Resin bonded has aggregates scattered on top of wet resin, creating a rougher, less permeable surface. Resin bound is more durable, permeable, and suitable for driveways and pathways.'
    }
  ];

  const toggleFAQ = (index: number) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };

  return (
    <>
      <Helmet>
        <title>Resin Driveways, Patios & Paving London | Expert Resin Bound Installers | Free Quotes</title>
        <meta name="description" content="Driveways, patios & paving by Resin Surfaces London. Premium resin bound installations with Vuba materials, 10+ years experience, SUDS compliance & ⭐⭐⭐⭐⭐ reviews. Free quotes across London, Surrey & Hertfordshire." />
        <meta name="keywords" content="resin driveways London, resin patios London, resin paving London, resin courtyard London, resin pathway installers London, resin driveway installers London, permeable paving London, resin patio contractors London, resin paving specialists London, SUDS compliant driveways London, resin patios Surrey, resin paving Hertfordshire, resin driveway quotes London, resin patio design London, resin paving company London, premium resin patios London" />
        <link rel="canonical" href="https://www.resinsurfaceslondon.co.uk/services" />
        
        {/* Open Graph */}
        <meta property="og:title" content="Resin Driveways, Patios & Paving | Resin Surfaces London" />
        <meta property="og:description" content="Expert resin bound driveways, patios & paving across London. 10+ years experience, premium Vuba materials & free quotes." />
        <meta property="og:image" content="https://www.resinsurfaceslondon.co.uk/images/resin-surfaces-bound-work1.jpeg" />
        <meta property="og:url" content="https://www.resinsurfaceslondon.co.uk/services" />
        <meta property="og:type" content="website" />
        
        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:title" content="Resin Driveways, Patios & Paving | Resin Surfaces London" />
        <meta property="twitter:description" content="Driveways, patios & paving installed with resin bound precision. 10+ years experience & free quotes." />
        <meta property="twitter:image" content="https://www.resinsurfaceslondon.co.uk/images/resin-surfaces-bound-work1.jpeg" />
        
        {/* Structured Data for Ratings */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "name": "Resin Driveways, Patios & Paving London",
            "description": "Professional resin bound driveway, patio and paving installation services across London",
            "provider": {
              "@type": "LocalBusiness",
              "name": "Resin Surfaces London",
              "url": "https://www.resinsurfaceslondon.co.uk",
              "telephone": "+44 7535 935779",
              "address": {
                "@type": "PostalAddress",
                "streetAddress": "Broadfield Broadhurst Gardens",
                "addressLocality": "London",
                "addressRegion": "London",
                "postalCode": "NW6 3BN",
                "addressCountry": "GB"
              }
            },
            "areaServed": {
              "@type": "City",
              "name": "London"
            },
            "serviceType": [
              "Resin Bound Driveways",
              "Resin Bound Patios", 
              "Resin Paving & Pathways",
              "Permeable Surfacing",
              "SUDS Compliant Surfacing"
            ],
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "5.0",
              "reviewCount": "247",
              "bestRating": "5",
              "worstRating": "1"
            },
            "review": [
              {
                "@type": "Review",
                "reviewRating": {
                  "@type": "Rating",
                  "ratingValue": "5",
                  "bestRating": "5"
                },
                "author": {
                  "@type": "Person",
                  "name": "Mark Stevens"
                },
                "datePublished": "2024-12-20",
                "reviewBody": "Outstanding resin surfacing services! The team installed our driveway and pathway perfectly. Professional, clean work and excellent customer service. 5 stars!"
              },
              {
                "@type": "Review",
                "reviewRating": {
                  "@type": "Rating",
                  "ratingValue": "5",
                  "bestRating": "5"
                },
                "author": {
                  "@type": "Person",
                  "name": "Jennifer Taylor"
                },
                "datePublished": "2024-12-15",
                "reviewBody": "Best resin surfacing company in London! Our pool deck looks amazing and the installation was flawless. Highly recommend their services."
              }
            ]
          })}
        </script>
      </Helmet>

      <HeroSectionComponent
        title="Resin Driveways, Patios & Paving"
        subtitle="From vehicle-ready driveways to seamless patios and paving, we deliver premium resin bound installations across London and the Home Counties."
        badge="Driveways • Patios • Paving • 10+ Year Warranty"
        primaryButtonText="Get Free Quote"
        primaryButtonLink="/contact/"
        secondaryButtonText="Call Now"
        secondaryButtonLink="tel:+447535935779"
      />

      <Section>
        <Container>
          <SectionTitle>Our Services</SectionTitle>
          <ServicesGrid>
            {services.map((service, index) => (
              <ServiceCard
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <ServiceIcon>
                  <service.icon size={40} />
                </ServiceIcon>
                <ServiceTitle>{service.title}</ServiceTitle>
                <ServiceDescription>{service.description}</ServiceDescription>
                <ServiceFeatures>
                  {service.features.map((feature, featureIndex) => (
                    <li key={featureIndex}>
                      <CheckCircle size={16} />
                      {feature}
                    </li>
                  ))}
                </ServiceFeatures>
                <ServiceButton to={service.link}>
                  Learn More
                  <ArrowRight size={16} />
                </ServiceButton>
              </ServiceCard>
            ))}
          </ServicesGrid>
        </Container>
      </Section>

      <PartnershipSection>
        <Container>
          <SectionTitle>Premium Materials from Industry Leaders</SectionTitle>
          <PartnershipsGrid>
            <PartnershipCard>
              <PartnershipLogo>
                <img src="/vuba_resin_logo_uk.png" alt="Vuba Surfaces - UK's #1 Resin Bound Surfacing Manufacturer" />
              </PartnershipLogo>
              <PartnershipDetails>
                <h3>Vuba Surfaces</h3>
                <p className="partner-tagline">UK's #1 Resin Bound Manufacturer since 2009</p>
                <p>
                  Our commitment to quality starts with our partnership with <a href="https://www.vubasurfaces.co.uk/" target="_blank" rel="noopener noreferrer">Vuba Surfaces</a>, 
                  the UK's leading resin bound surfacing manufacturer since 2009. This ensures every project we complete uses only the highest quality, 
                  BBA-certified materials that meet the strictest industry standards.
                </p>
                <PartnershipBenefits>
                  <PartnershipBenefit>
                    <CheckCircle size={16} />
                    <span>BBA Certified Quality</span>
                  </PartnershipBenefit>
                  <PartnershipBenefit>
                    <CheckCircle size={16} />
                    <span>15+ Years Manufacturing Experience</span>
                  </PartnershipBenefit>
                  <PartnershipBenefit>
                    <CheckCircle size={16} />
                    <span>Premium Aggregate Blends</span>
                  </PartnershipBenefit>
                </PartnershipBenefits>
                <PartnershipCTA>
                  <a 
                    href="https://www.vubasurfaces.co.uk/" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="partner-link"
                  >
                    Learn More About Vuba <ArrowRight size={16} />
                  </a>
                </PartnershipCTA>
              </PartnershipDetails>
            </PartnershipCard>

            <PartnershipCard>
              <PartnershipLogo>
                <img src="/gcl_products_logo_uk.png" alt="GCL Products - Groundworks, Construction & Landscaping" />
              </PartnershipLogo>
              <PartnershipDetails>
                <h3>GCL Products</h3>
                <p className="partner-tagline">Groundworks, Construction & Landscaping</p>
                <p>
                  Our partnership with <a href="https://www.gclproducts.co.uk/" target="_blank" rel="noopener noreferrer">GCL Products</a> 
                  provides access to a comprehensive range of groundworks, construction, and landscaping products. 
                  This ensures we have reliable access to all the materials needed for complete project delivery.
                </p>
                <PartnershipBenefits>
                  <PartnershipBenefit>
                    <CheckCircle size={16} />
                    <span>UK-Wide Delivery</span>
                  </PartnershipBenefit>
                  <PartnershipBenefit>
                    <CheckCircle size={16} />
                    <span>Comprehensive Product Range</span>
                  </PartnershipBenefit>
                  <PartnershipBenefit>
                    <CheckCircle size={16} />
                    <span>Reliable Supply Chain</span>
                  </PartnershipBenefit>
                </PartnershipBenefits>
                <PartnershipCTA>
                  <a 
                    href="https://www.gclproducts.co.uk/" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="partner-link"
                  >
                    Learn More About GCL <ArrowRight size={16} />
                  </a>
                </PartnershipCTA>
              </PartnershipDetails>
            </PartnershipCard>
          </PartnershipsGrid>
        </Container>
      </PartnershipSection>

      <Section>
        <Container>
          <SectionTitle>Our Installation Process</SectionTitle>
          <ProcessSection>
            <ProcessGrid>
              {processSteps.map((step, index) => (
                <ProcessStep
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <StepNumber>{step.number}</StepNumber>
                  <StepTitle>{step.title}</StepTitle>
                  <StepDescription>{step.description}</StepDescription>
                </ProcessStep>
              ))}
            </ProcessGrid>
          </ProcessSection>
        </Container>
      </Section>

      <FAQSection>
        <Container>
          <SectionTitle>Frequently Asked Questions About Our Services</SectionTitle>
          <SectionSubtitle>
            Get answers to common questions about our resin surfacing services, installation process, and maintenance
          </SectionSubtitle>
          <FAQGrid>
            {faqs.map((faq, index) => (
              <FAQItem
                key={index}
                className={openFAQ === index ? 'active' : ''}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div 
                  className={`question ${openFAQ === index ? 'active' : ''}`}
                  onClick={() => toggleFAQ(index)}
                >
                  {faq.question}
                  <ChevronDown size={20} />
                </div>
                <div className={`answer ${openFAQ === index ? 'active' : ''}`}>
                  {faq.answer}
                </div>
              </FAQItem>
            ))}
          </FAQGrid>
        </Container>
      </FAQSection>

      <Section>
        <Container>
          <div style={{ textAlign: 'center' }}>
            <SectionTitle>Ready to Transform Your Space?</SectionTitle>
            <p style={{ 
              fontSize: '1.25rem', 
              color: brandTheme.colors.secondary[600], 
              marginBottom: '3rem',
              maxWidth: '600px',
              margin: '0 auto 3rem auto',
              lineHeight: brandTheme.typography.lineHeight.relaxed
            }}>
              Get a free quote today and discover how our professional resin surfacing can enhance your property.
            </p>
            <CTAButton to="/contact/">
              Get Free Quote
              <ArrowRight size={20} />
            </CTAButton>
          </div>
        </Container>
      </Section>
    </>
  );
};

export default ServicesPage;
